<template>
  <on-click-outside :action="() => searching = false">
    <div class="relative w-full">
      <span
        class="absolute inset-y-0 flex items-center pl-3 text-xs font-semibold text-label"
        >{{ label }}:
      </span>
      <input
        v-model="search"
        ref="pickup-location-input"
        @keyup="searchRoutesAndLocations"
        type="text"
        class="flex-grow w-full h-full px-3 py-4 text-xs font-semibold text-med pl-14 input--neutral placeholder-neutral-type-1"
        :class="locationClass"
        :placeholder="placeholder"
        @focus="searching = true"
      />
      <div
        
        class="absolute z-10 w-full mt-1 overflow-x-auto bg-widget-dropdown-active text-widget-dropdown-active-body max-h-96"
        :class="locationClass"
        style="min-height: 300px"
        v-if="searching"
      >
        <div class="px-2 py-2 bg-active" v-if="findByLocationEnabled">
          <p class="text-xs font-medium">Find by Location</p>
        </div>
        <div class="mt-2 space-y-4" v-if="findByLocationEnabled">
          <template v-if="locations.length > 0">
            <button
              type="button"
              class="flex px-2 space-x-3 text-left"
              v-for="(location, index) in locations"
              :key="index"
              @click="selectLocation(location)"
            >
              <div
                class="flex-shrink-0 w-2 h-2 mt-1 rounded-full bg-active"
              ></div>
              <div class="text-xs font-medium leading-relaxed">
                <mark
                  v-if="location.matched_length"
                  class="bg-transparent text-active"
                  >{{ location.label.slice(0, location.matched_length) }}</mark
                >{{ location.label.slice(location.matched_length || 0) }}
              </div>
            </button>
          </template>
          <div v-else class="text-xs font-medium text-center">
            No location found
          </div>
        </div>
        <div class="px-2 py-2 mt-2 bg-active" v-if="!findByLocationEnabled">
          <p class="text-xs font-medium">Find by Bus Stops</p>
        </div>
        <div class="mt-2 space-y-4" v-if="!findByLocationEnabled">
          <button
            type="button"
            class="flex px-2 space-x-3 text-left"
            v-for="busStop in filteredBusStops"
            :key="busStop.id"
            @click="selectLocation(busStop)"
          >
            <div
              class="flex-shrink-0 w-2 h-2 mt-1 rounded-full bg-active"
            ></div>
            <div class="text-xs font-medium leading-relaxed">
              {{ busStop.label }}
            </div>
          </button>
        </div>
      </div>
    </div>
  </on-click-outside>
</template>

<script>
import { debounce } from "@/services/utils";
import OnClickOutside from "../OnClickOutside.vue";

export default {
  inject: ['appContext'],
  components: { OnClickOutside },
  props: {
    value: Object,
    busStops: {
      type: Array,
    },
    label: String,
    placeholder: String,
    findByLocationEnabled: {type: Boolean, default: true},
    locationClass:{required: false, default:'', type: String},
  },
  data() {
    return {
      search: "",
      searching: false,
      locations: [],
      autocomplete: null,
      places: null,
      googleMapsApi: null,
    };
  },
  created() {
    this.setupPlacesApi();

    if (this.value) {
      this.search = this.value.label;
    }
  },
  destroyed() {
    this.autocomplete = this.googleMapsApi = null;
  },
  computed: {
    filteredBusStops() {
      return this.busStops.filter((route) =>
        route.label.toLowerCase().includes(this.search.toLowerCase())
      );
    },
  },
  methods: {
    setupPlacesApi() {
      return this.$gmapApiPromiseLazy().then((google) => {
        this.googleMapsApi = google;

        this.autocomplete = new google.maps.places.AutocompleteService();
        this.places = new google.maps.places.PlacesService(
          document.createElement("div")
        );
      });
    },
    searchRoutesAndLocations: debounce(async function ($event) {
      const input = $event ? $event.target.value : this.search;

      if (input.length === 0 || !this.findByLocationEnabled) {
        return;
      }
      
      if (!this.autocomplete) {
        await this.setupPlacesApi();
      }

      this.autocomplete.getQueryPredictions(
        {
          input: $event.target.value,
          location: new this.googleMapsApi.maps.LatLng(
            this.appContext.userLocation.latitude,
            this.appContext.userLocation.longitude
          ),
          radius: 20000,
          types: ["geocode"],
          componentRestrictions: {
            country: "NG",
          },
        },
        this.onPlaceChanged
      );
    }, 500),
    selectLocation(location) {
      if (location.is_autocomplete) {
        this.places.getDetails(
          {
            placeId: location.place_id,
            fields: ["geometry"],
          },
          (place) => {
            const latitude = place.geometry.location.lat();
            const longitude = place.geometry.location.lng();

            this.$emit("location-selected", {
              id: `${latitude},${longitude}`,
              label: location.label,
              latitude,
              longitude,
              is_autocomplete: true,
            });
          }
        );
      } else {
        this.$emit("location-selected", {
          ...location,
        });
      }

      this.search = location.label;

      this.searching = false;
    },
    onPlaceChanged(predictions) {
      if (!predictions) {
        return;
      }
      
      this.locations = predictions.map((prediction) => ({
        label: prediction.description,
        place_id: prediction.place_id,
        is_autocomplete: true,
        matched_length:
          prediction.matched_substrings.length > 0
            ? prediction.matched_substrings[0].length
            : null,
      }));
    },
  },
};
</script>